import CopyButton from "@components/buttons/CopyButton";
import React, { useEffect, useRef, useState } from "react";
import ConstantFunctions from "../../utils/constants_functions";
import {
  GetRefundDetailsData,
  GetRefundDetailsResponse,
} from "@services/models/Refund/GetRefundDetails";
import { refundsServices } from "@services/apis/Refunds";
import ImageUpload from "@assets/refunds/image-upload.png";
import RefundDetailsSkeleton from "./ components/RefundDetailsSkeleton";
import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import CustomButton from "@components/buttons/CustomButton";
import useSnackbarMessage from "../../utils/HandleSuccessErrorSnackbars";
import { useMediaQuery } from "react-responsive";
import { useLocation, useNavigate } from "react-router-dom";
import AppBarMobile from "@components/appbars/AppBarMobile";
import LabelRow from "@components/rowcomponents/LabelRow";

interface RefundDetailsProps {
  refundId: string;
  width?: string;
}

export const RefundDetails: React.FC<RefundDetailsProps> = ({
  refundId,
  width = "w-1/4",
}) => {
  const { handleError, handleSuccess } = useSnackbarMessage();
  const isMobile = useMediaQuery({ maxWidth: 640 });
  const navigate = useNavigate();
  const [data, setData] = useState<GetRefundDetailsData>();
  const [error, setError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const constFunctions = new ConstantFunctions();

  const [notes, setNotes] = useState<string>("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const location = useLocation();
  const refundIdState = location.state?.refundId;

  console.log("Refund ID from state:", refundIdState);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setSelectedFile(file);

      // Create preview for image
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const removeImage = () => {
    setSelectedFile(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response: GetRefundDetailsResponse =
        await refundsServices.getRefundDetails({
          refundId: isMobile ? refundIdState : refundId,
        });
      setData(response.data);
    } catch (error: any) {
      setError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [refundId]);

  const handleSubmit = async () => {
    if (!selectedFile) {
      return;
    }
    setIsSubmitting(true);
    try {
      await refundsServices.submitDetails({
        cashierNotes: notes,
        file: selectedFile,
        refundId: isMobile ? refundIdState : refundId,
      });
      handleSuccess({ message: "Refund submit successfully" });
      if (isMobile) {
        navigate("/");
      }
    } catch (error) {
      handleError({ message: "Failed to submit refund" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownload = (imageUrl: string) => {
    const link = document.createElement("a");
    link.href = imageUrl;
    link.download = "downloaded-image.jpg";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (error) {
    return <div>Fetch error</div>;
  }

  if (isLoading) {
    return isMobile ? "Loading..." : <RefundDetailsSkeleton />;
  }

  return (
    <>
      {isMobile && <AppBarMobile title={"Refund Details"} path={""} />}
      <div
        className={`flex flex-col gap-[12px] mt-[20px] ${width} ${
          isMobile ? "px-[16px] mt-[84px]" : "mr-[16px]"
        }`}
      >
        <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
          {/* Trade Header */}
          <div className="flex flex-row justify-between">
            <div className="bg-bg-page-body py-[4px] px-[12px] text-text-base font-bold text-[10px] rounded">
              Trade ID: {data?.tradeId}
            </div>
            <div
              className={`flex text-[10px] h-[20px] px-[12px] font-bold text-center items-center rounded-lg ${
                data?.tradeType === "BUY"
                  ? "bg-bg-status-green"
                  : "bg-bg-status-red"
              } text-text-base`}
            >
              {data?.tradeType}
            </div>
          </div>

          {/* MID | MUID Section */}
          {data?.MUID && (
            <LabelRow
              label="MID | MUID"
              valueComp={
                <div className="flex flex-row gap-[4px] items-center">
                  <span className="text-text-base text-[12px] font-normal">
                    {data?.MID}
                  </span>
                  <CopyButton
                    copyText={data?.MID.toString() ?? ""}
                    copyMessage="Copied to clipboard"
                  />
                  <span className="text-text-base text-[12px] font-normal">
                    |
                  </span>
                  <span className="text-text-base text-[12px] font-normal">
                    {data?.MUID}
                  </span>
                  <CopyButton
                    copyText={data?.MUID.toString() ?? ""}
                    copyMessage="Copied to clipboard"
                  />
                </div>
              }
            />
          )}

          {/* MOID */}
          <LabelRow
            label="MOID"
            value={data?.MOID ?? ""}
            isCopy={!!data?.MOID}
          />

          {/* UTR Number */}
          <LabelRow
            label="Cx UTR Number"
            value={data?.UTR ?? "- -"}
            isCopy={!!data?.UTR}
          />

          {/* Cx UPI ID */}
          <LabelRow
            label="Cx UPI ID"
            value={data?.upiId ? data.upiId : "- -"}
            isCopy={!!data?.upiId}
          />

          {/* Trade Date */}
          <LabelRow
            label="Trade Date & Time"
            value={constFunctions.formatDateTime(data?.tradeTime!)}
          />

          {/* Refund Date */}
          <LabelRow
            label="Refund Date & Time"
            value={constFunctions.formatDateTime(data?.refundRequestTime!)}
          />

          {/* Refund Closed */}
          <LabelRow
            label="Refund Updated"
            value={constFunctions.formatDateTime(data?.refundUpdateTime!)}
          />
        </div>
        <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
          <LabelRow
            label="Screenshot from cx"
            valueComp={
              <div className="flex items-center gap-[4px] cursor-pointer">
                <img src={ImageUpload} alt="Upload icon" />
                <div
                  className="h-[16px] w-[16px] rounded-[2px] bg-bg-button-secondary flex items-center justify-center cursor-pointer"
                  onClick={() => {
                    handleDownload(data?.merchantImage!);
                  }}
                >
                  <span className="material-symbols-outlined text-[12px] text-text-base">
                    download
                  </span>
                </div>
              </div>
            }
          />

          {/* Fiat Amount */}
          <LabelRow
            label="Fiat Amount"
            value={`${data?.fiatAmount} ${data?.fiatCurrencySymbol}`}
          />
        </div>
        {data?.status === "ASSIGNED TO CASHIER" && (
          <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle rounded-lg mb-[80px]">
            <div className="flex flex-row items-center justify-between">
              <span className="text-text-base text-[12px] font-normal">
                Upload Cx Payment Screenshot
              </span>
              <div className="flex flex-row items-center gap-[4px]">
                {imagePreview ? (
                  <div className="flex items-center gap-[4px]">
                    <div
                      className="h-[16px] w-[16px] rounded-[2px] bg-bg-button-secondary flex items-center justify-center cursor-pointer"
                      onClick={triggerFileInput}
                    >
                      <span className="material-symbols-outlined text-[12px] text-text-base">
                        upload
                      </span>
                    </div>
                    <div
                      className="h-[16px] w-[16px] rounded-[2px] bg-bg-button-secondary flex items-center justify-center cursor-pointer"
                      onClick={removeImage}
                    >
                      <span className="material-symbols-outlined text-[12px] text-text-base">
                        delete
                      </span>
                    </div>
                  </div>
                ) : (
                  <div
                    className="flex items-center gap-[4px] cursor-pointer"
                    onClick={triggerFileInput}
                  >
                    <img src={ImageUpload} alt="Upload icon" />
                    <div className="h-[16px] w-[16px] rounded-[2px] bg-bg-button-secondary flex items-center justify-center">
                      <span className="material-symbols-outlined text-[12px] text-text-base">
                        upload
                      </span>
                    </div>
                  </div>
                )}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  className="hidden"
                  accept="image/*"
                />
              </div>
            </div>
            {imagePreview && (
              <div className="flex justify-center items-center w-full">
                <img
                  src={imagePreview}
                  alt="Payment screenshot"
                  className="h-[180px] w-[180px] object-cover rounded-[4px]"
                />
              </div>
            )}
            <div className="flex flex-row items-center justify-between">
              <span className="text-text-base text-[12px] font-normal">
                Notes
              </span>
              <GlobalTextInputField
                placeholder="Enter here"
                value={notes}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setNotes(e.target.value)
                }
              />
            </div>
          </div>
        )}
        {data?.status === "ASSIGNED TO CASHIER" && (
          <div
            className={`${
              isMobile
                ? "fixed bottom-0 bg-bg-page-body z-10 py-[16px] pr-[32px] w-full"
                : "fixed bottom-0 right-0 w-1/4 bg-bg-page-body z-10 p-[16px]"
            }`}
          >
            <CustomButton
              text={"SUBMIT DETAILS"}
              onClick={handleSubmit}
              isActive={!!selectedFile && notes.length > 0}
              isButtonLoading={isSubmitting}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default RefundDetails;
